import React, { useState } from 'react';
import { Icon, Spinner } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import classNames from 'classnames';
import {
  ScrollMenu,
  publicApiType
} from 'react-horizontal-scrolling-menu';

import { categoryTypeColor } from '../../../../../utils/scoutingUtils';
import { CategoryTypeItem } from '../CategoryTypeItem/CategoryTypeItem';
import { CategoryAttribute } from '../CategoryAttribute/CategoryAttribute';
import { DragDealer } from './DragDealer';
import {
  CategoryType,
  CategoryAttributeLevelSelected,
  CategoryAttribute as CategoryAttributeType,
} from '../../../../../types';

import styles from '../scoutingForm.css';

const categoryTypesIcons = {
  WEED: {
    icon: Icon.set.WEED,
    color: categoryTypeColor('WEED'),
  },
  DISEASE: {
    icon: Icon.set.DISEASE,
    color: categoryTypeColor('DISEASE')
  },
  INSECT: {
    icon: Icon.set.INSECT,
    color: categoryTypeColor('INSECT')
  },
  NUTRIENT: {
    icon: Icon.set.NUTRIENTS,
    color: categoryTypeColor('NUTRIENT')
  },
  WATER: {
    icon: Icon.set.WATER,
    color: categoryTypeColor('WATER')
  },
  OTHER: {
    icon: Icon.set.OTHER_PIN,
    color: categoryTypeColor('OTHER'),
    className: styles.noPadding,
    containerClassname: styles.noMarginRight,
  },
}

export type CategorySelectorProps = {
  categoryTypes: CategoryType[];
  categoryTypeSelected: CategoryType | null;
  setCategorySelected: React.Dispatch<React.SetStateAction<CategoryType | null>>;
  attributes: CategoryAttributeType[] | null;
  attributesSelected: CategoryAttributeLevelSelected;
  setAttributesSelected: React.Dispatch<React.SetStateAction<CategoryAttributeLevelSelected>>
  loading: boolean;
}

export function CategorySelector({
  categoryTypes,
  categoryTypeSelected,
  attributes,
  attributesSelected,
  setCategorySelected,
  setAttributesSelected,
  loading,
}: CategorySelectorProps) {
  const [collapsed, setCollapsed] = useState(false);
  const dragState = React.useRef(new DragDealer());

  const collapsableClass = classNames(
    styles.categorySelectorAttributesTitleCollapsableButton,
    collapsed ? styles.categorySelectorAttributesTitleCollapsableButtonActive : '');

  const handleDrag =
    ({ scrollContainer }: publicApiType) =>
    (ev: React.MouseEvent) =>
      dragState.current.dragMove(ev, (posDiff) => {
        if (scrollContainer.current) {
          scrollContainer.current.scrollLeft += posDiff;
        }
      });
  const onMouseDown = React.useCallback(() => dragState.current.dragStart, []);
  const onMouseUp = React.useCallback(() => dragState.current.dragStop, []);
  const onMouseLeave = React.useCallback(() => dragState.current.dragStop, []);

  const onClickCategory = (type: CategoryType) => {
    if (dragState.current.dragging) {
      return;
    }

    setAttributesSelected({});

    if (type === categoryTypeSelected) {
      setCategorySelected(null)
    } else {
      setCategorySelected(type);
    }
  }

  const toggleCollapsable = () => {
    setCollapsed(c => !c)
  }

  return (
    <div className={styles.categorySelector} data-test-id='category-selector'>
      <div className={classNames(styles.categoryItem, styles.categorySelectorTitle)} data-test-id='category-selector-title'>
        {getMessage('enhancedScouting.form.category')}
      </div>
      {loading ? (
        <div className={styles.categorySelectorIsLoading}>
          <Spinner size='64' />
        </div> 
      ) : (
        <ScrollMenu
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={handleDrag}
          scrollContainerClassName={classNames(styles.categoryItem, styles.categorySelectorContent)}
        >
          {categoryTypes.map((categoryType) => (
            <CategoryTypeItem
              key={categoryType}
              categoryType={categoryType}
              selected={categoryTypeSelected === categoryType}
              testId={`category-item-${categoryType.toLowerCase()}`}
              onClick={() => { onClickCategory(categoryType) }} 
              {...categoryTypesIcons[categoryType]}
            />
          ))}
        </ScrollMenu>
      )}

      {(categoryTypeSelected && attributes) ? (
        <div className={styles.categorySelectorAttributes} data-test-id='category-attribute'>
          <div className={styles.categorySelectorAttributesRow} data-test-id='category-attribute-title-row'>
            <div 
              className={classNames(styles.categorySelectorAttributesTitleContainer, styles.noMarginBottom)}
              data-test-id='category-attribute-title-container'
            >
              <div className={styles.categorySelectorAttributesTitle} data-test-id='category-attribute-title'>
                {getMessage('enhancedScouting.form.attributes')}
              </div>
              <Icon
                className={collapsableClass}
                glyph={Icon.set.ARROW_DOWN}
                onClick={toggleCollapsable}
                testId='category-title-collapsable-icon'
              />
            </div>
          </div>
          {!collapsed ? (
            attributes.map((attribute) => (
              <CategoryAttribute 
                key={attribute.attribute}
                {...attribute}
                attributesSelected={attributesSelected}
                setAttributesSelected={setAttributesSelected}
                testId={`category-attribute-${attribute.attribute}`}
              />
            ))
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
